<template>
    <Section class="document" :class="mainClass">
        <Loading v-if="isLoading"/>
        <template v-if="hasError">
            <div class="document-error">
                <h1>
                    {{$t('document.error.title')}}
                </h1>
                <h2>
                    {{errorData}}
                </h2>
            </div>
        </template>
        <template v-if="!hasError">
            <PublicDocumentTopBackground :item="topCenterSmartAd"/>
            <PublicDocumentHeader :publicDocument="document"/>

            <div v-if="showTopSmartAds" class="document-smartads top">
                <PublicDocumentSmartAd :smartad="topLeftSmartAd" _data-aos="fade-up" _data-aos-delay="850" @click="sendUserDocumentActivity(UserDocumentActivityEnum.SmartAdTopLeftClick, $event.type)"/>
                <PublicDocumentSmartAd :smartad="topRightSmartAd" _data-aos="fade-up" _data-aos-delay="950" @click="sendUserDocumentActivity(UserDocumentActivityEnum.SmartAdTopRightClick, $event.type)"/>
            </div>

            <div class="document-container" ref="documentContainer">
                <div class="document-container-frame">
                    <div class="document-secured-header">
                        <div class="document-secured-header-logo">
                            <i :class="securedIcon" />
                        </div>
                        <span v-html="securedTitle"></span>
                        <PrimaryButton v-if="isVerified" @click="showIntegrityDialog" class="small primary"><span>{{$t('document.actions.showIntegrity')}}</span><i class="s-icon-arrow-right"/></PrimaryButton>
                    </div>
                    <!-- <div> -->
                        <ImageLoader
                            :default="require('@/assets/v3/img/document-default.png')"
                            :src="documentPreviewUrl"
                            :preventCache="true"
                            class="document-container-frame-picture"
                        />

                        <div class="document-keywords">
                            <span
                            v-for="item in keywords"
                            :key="item"
                            >
                                {{item}}
                            </span>
                        </div>
                        <div class="document-actions">
                            <PrimaryButton @click="dowloadBlockchainReceipt" v-if="showBlockchainActions" class="primary">
                                <span>{{$t('document.actions.downloadBlockchainReceipt')}}</span>
                                <img :src="require('@/assets/v3/img/icons/document.svg')"/>
                            </PrimaryButton>
                            <PrimaryButton slot="reference" @click="showBlockchainData" v-if="showBlockchainActions" class="primary">
                                <span>{{$t('document.actions.validateBlockchain')}}</span>
                                <img :src="require('@/assets/v3/img/icons/document-zoom.svg')"/>
                            </PrimaryButton>
                            <PrimaryButton @click="downloadBadge" v-if="isBadge" class="primary blink">
                                <span>{{$t('document.actions.downloadBadge')}}</span>
                                <img :src="require('@/assets/v3/img/icons/picture.svg')"/>
                            </PrimaryButton>
                            <PrimaryButton @click="dowloadDocument" class="primary">
                                <span>{{isBadge ? $t('document.actions.downloadBadgeAssertion') : $t('document.actions.downloadDocument')}}</span>
                                <img :src="require('@/assets/v3/img/icons/document-lock.svg')"/>
                            </PrimaryButton>
                        </div>

                        <div v-if="isBadge && badgeClass" class="document-badge-metadata">
                            <h2 class="document-badge-metadata-title">{{ badgeClass.name }}</h2>
                            <p v-if="badgeClass.description" class="document-badge-metadata-field">
                                <strong>{{$t('document.badge.description')}}:</strong> {{ badgeClass.description }}
                            </p>
                            <p v-if="badgeCriteriaNarrative" class="document-badge-metadata-field">
                                <strong>{{$t('document.badge.criteria')}}:</strong> {{ badgeCriteriaNarrative }}
                            </p>
                            <div v-if="badgeTags && badgeTags.length" class="document-badge-metadata-tags">
                                <span v-for="tag in badgeTags" :key="tag">{{ tag }}</span>
                            </div>
                        </div>

                        <div class="document-recipient-signature" v-if="showBlockchainActions2">
                            {{$t('document.recipient.signature.title')}}
                        </div>

                        <PublicDocumentShareButtons :url="currentUrl" class="vertical"/>
                        <PublicDocumentShareButtons :url="currentUrl" class="boxed"/>
                    <!-- </div> -->
                </div>
            </div>

            <div v-if="showBottomSmartAds" class="document-smartads bottom">
            <PublicDocumentSmartAd :smartad="bottomLeftSmartAd" />
            <PublicDocumentSmartAd :smartad="bottomRightSmartAd" />
            </div>

            <!-- BLOCKCHAIN DIALOG -->

            <el-dialog
                :visible.sync="blockchainDataVisible"
                :show-close="false"
                custom-class="document-blockchain-data-dialog"
                :modal-append-to-body="true"
                :append-to-body="true"
            >
                <h1><i class="s-icon-lock"/>{{$t('document.blockchain.popup.title')}}</h1>
                <span v-html="$t('document.blockchain.popup.desc')"></span>
                <div class="document-blockchain-data-dialog-proof">
                    <i class="s-icon-check_ok"/>
                    <span>{{blockchainVerificationStatus}}</span>
                    @
                    <span>{{blockchainVerificationTimestamp}}</span>
                </div>
            </el-dialog>

            <!-- INTEGRITY DIALOG -->
            <el-dialog
                :visible.sync="integrityDialogVisible"
                :show-close="false"
                custom-class="document-integrity-dialog"
                :modal-append-to-body="true"
                :append-to-body="true"
            >
                <h1>
                    <i class="s-icon-smart-certificate-logo"/>
                    {{$t(`${integrityDialogKeyPrefix}.title`)}}
                </h1>
                <span>
                    {{$t(`${integrityDialogKeyPrefix}.subTitle`)}}
                </span>

                <h2>
                    <i class="s-icon-check_ok"/>
                    <b>{{$t(`${integrityDialogKeyPrefix}.steps[0].title`)}}</b>
                </h2>
                <span>
                    {{$t(`${integrityDialogKeyPrefix}.steps[0].desc`)}}
                </span>

                <h2>
                    <i class="s-icon-check_ok"/>
                    <b>{{$t(`${integrityDialogKeyPrefix}.steps[1].title`)}}</b>
                </h2>
                <span>
                    {{$t(`${integrityDialogKeyPrefix}.steps[1].desc`)}}
                </span>

                <h2>
                    <i class="s-icon-check_ok"/>
                    <b>{{$t(`${integrityDialogKeyPrefix}.steps[2].title`)}}</b>
                </h2>
                <span>
                    {{$t(`${integrityDialogKeyPrefix}.steps[2].desc`)}}
                </span>
            </el-dialog>
        </template>
    </Section>
</template>

<i18n>
{
    "en": {
        "document": {
            "error": {
                "title": "An error occurred",
                "codes": {
                    "_1": "An error has occurred. The document you are trying to access either does not exist or has been removed from our system.",
                    "_2": "The document you are trying to access does not exist.",
                    "_3": "This document was revoked by {organizationName}. Reason: {reason}"
                }
            },
            "securedTitle": "This document is secured by <b>Smart Certificate</b>",
            "notSecuredTitle": "This document is not yet verified by <b>Smart Certificate</b>. This can take a couple of days.",
            "actions": {
                "showIntegrity": "See how",
                "downloadBlockchainReceipt": "Download blockchain receipt",
                "validateBlockchain": "Validate blockchain reception",
                "downloadDocument": "Download secure PDF",
                "downloadBadge": "Download badge",
                "downloadBadgeAssertion": "Download badge assertion (JSON)"
            },
            "badge": {
                "description": "Description",
                "criteria": "Criteria",
                "tags": "Tags"
            },
            "recipient": {
                "signature": {
                    "title": "The recipient has signed this document"
                }
            },
            "blockchain": {
                "popup": {
                    "title": "Blockchain verification",
                    "desc": "A proof of validity and integrity for each document is stored into the Bitcoin blockchain. This ensures that all of our documents have an <b>unalterable</b> proof of their integrity stored on a public and decentralized platform. You can find below the status of the verification and the timestamp, the time at which the document integrity was stored into the blockchain."
                }
            },
            "integrity-dialog": {
                "document": {
                    "title": "What is a Smart Certificate?",
                    "subTitle": "It is a document that certifies 3 fundamental things:",
                    "steps": [
                        {
                            "title": "Integrity",
                            "desc": "We guarantee that the document has not been modified since its creation. It contains a valid signature and an immutable footprint stored on the Bitcoin blockchain, providing a decentralized method to verify its integrity."
                        },
                        {
                            "title": "Authenticity",
                            "desc": "We certify the identity of the issuer. This ensures that the organization and its members are legitimate and authorized to issue this document."
                        },
                        {
                            "title": "Validity",
                            "desc": "We ensure the real-time validity of the document. Any change, such as an update, revocation, expiration, or validity extension, is instantly reflected on this page."
                        }
                    ]
                },
                "badge": {
                    "title": "What is a Smart Certificate badge?",
                    "subTitle": "It is a badge that certifies 3 fundamental things:",
                    "steps": [
                        {
                            "title": "Integrity",
                            "desc": "We guarantee that the badge has not been modified since its creation. It follows the Open Badge 2.0 standard."
                        },
                        {
                            "title": "Authenticity",
                            "desc": "We certify the identity of the issuer. This ensures that the organization and its members are legitimate and authorized to issue this badge."
                        },
                        {
                            "title": "Validity",
                            "desc": "We ensure the real-time validity of the badge. Any change, such as an update, revocation, expiration, or validity extension, is instantly reflected on this page."
                        }
                    ]
                }
            }
        }
    },
    "es": {
        "document": {
            "error": {
                "title": "Se ha producido un error",
                "codes": {
                    "_1": "Se ha producido un error. El documento al que intenta acceder no existe o ha sido eliminado de nuestro sistema.",
                    "_2": "El documento al que intenta acceder no existe.",
                    "_3": "Este documento ha sido revocado por {organizationName}. Motivo: {reason}"
                }
            },
            "securedTitle": "Este documento está protegido por <b>un Smart Certificate</b>",
            "notSecuredTitle": "Este documento aún no ha sido verificado por <b>Smart Certificate</b>. Esto puede tardar un par de días.",
            "actions": {
                "showIntegrity": "Descubre cómo",
                "downloadBlockchainReceipt": "Descargar recibo blockchain",
                "validateBlockchain": "Validar la recepción de la cadena de bloques",
                "downloadDocument": "Descargar PDF seguro",
                "downloadBadge": "Descargar insignia",
                "downloadBadgeAssertion": "Descargar insignia (JSON)"
            },
            "badge": {
                "description": "Descripción",
                "criteria": "Criterios",
                "tags": "Etiquetas"
            },
            "recipient": {
                "signature": {
                    "title": "El destinatario ha firmado este documento"
                }
            },
            "blockchain": {
                "popup": {
                    "title": "Verificación Blockchain",
                    "desc": "La prueba de validez e integridad de cada documento se almacena en la cadena de bloques de Bitcoin. Esto garantiza que todos nuestros documentos tengan una prueba <b>inalterable</b> de su integridad almacenada en una plataforma pública y descentralizada. A continuación encontrará el estado de la verificación y la marca de tiempo, la hora en la que la integridad del documento se almacenó en la cadena de bloques."
                }
            },
            "integrity-dialog": {
                "document": {
                    "title": "¿Qué es un Smart Certificate?",
                    "subTitle": "Es un documento que certifica 3 cosas fundamentales:",
                    "steps": [
                        {
                            "title": "Integridad",
                            "desc": "Garantizamos que el documento no ha sido modificado desde su creación. Contiene una firma válida y una huella digital inmutable almacenada en la cadena de bloques de Bitcoin, lo que ofrece un método descentralizado para verificar su integridad."
                        },
                        {
                            "title": "Autenticidad",
                            "desc": "Certificamos la identidad del emisor. Esto garantiza que la organización y sus miembros son legítimos y están autorizados para emitir este documento."
                        },
                        {
                            "title": "Vigencia",
                            "desc": "Garantizamos la validez en tiempo real del documento. Cualquier cambio, como una actualización, revocación, caducidad o prórroga de la validez, se refleja al instante en esta página."
                        }
                    ]
                },
                "badge": {
                    "title": "¿Qué es una insignia Smart Certificate?",
                    "subTitle": "Es una insignia que certifica 3 aspectos fundamentales:",
                    "steps": [
                        {
                            "title": "Integridad",
                            "desc": "Garantizamos que la insignia no ha sido modificada desde su creación. Cumple con el estándar Open Badge 2.0."
                        },
                        {
                            "title": "Autenticidad",
                            "desc": "Certificamos la identidad del emisor. Esto garantiza que la organización y sus miembros son legítimos y están autorizados para emitir esta insignia."
                        },
                        {
                            "title": "Vigencia",
                            "desc": "Garantizamos la validez en tiempo real de la insignia. Cualquier cambio, como una actualización, revocación, caducidad o prórroga de la validez, se refleja al instante en esta página."
                        }
                    ]
                }
            }
        }
    },
    "fr": {
        "document": {
            "error": {
                "title": "Une erreur est survenue",
                "codes": {
                    "_1": "Une erreur est survenue. Le document auquel vous essayez d'accéder n'existe pas ou a été supprimé de notre système.",
                    "_2": "Le document auquel vous essayez d'accéder n'existe pas.",
                    "_3": "Ce document a été révoqué par {organizationName}. Motif : {reason}"
                }
            },
            "securedTitle": "Ce document est sécurisé par <b>Smart Certificate</b>",
            "notSecuredTitle": "Ce document n'est pas encore vérifié par <b>Smart Certificate</b>. Cela peut prendre quelques jours.",
            "actions": {
                "showIntegrity": "Découvrez comment",
                "downloadBlockchainReceipt": "Télécharger le reçu de la blockchain",
                "validateBlockchain": "Valider la réception de la blockchain",
                "downloadDocument": "Télécharger le PDF sécurisé",
                "downloadBadge": "Télécharger le badge",
                "downloadBadgeAssertion": "Télécharger l'attestation du badge (JSON)"
            },
            "badge": {
                "description": "Description",
                "criteria": "Critères",
                "tags": "Mots-clés"
            },
            "recipient": {
                "signature": {
                    "title": "Le destinataire a signé ce document"
                }
            },
            "blockchain": {
                "popup": {
                    "title": "Vérification de la blockchain",
                    "desc": "Une preuve de validité et d'intégrité pour chaque document est stockée dans la blockchain Bitcoin. Cela garantit que tous nos documents ont une preuve d'intégrité <b>inaltérable</b> stockée sur une plateforme publique et décentralisée. Vous trouverez ci-dessous l'état de la vérification et l'horodatage, c'est-à-dire l'heure à laquelle l'intégrité du document a été stockée dans la blockchain."
                }
            },
            "integrity-dialog": {
                "document": {
                    "title": "Qu'est-ce qu'un certificat intelligent ?",
                    "subTitle": "C'est un document qui certifie 3 éléments fondamentaux :",
                    "steps": [
                        {
                            "title": "Intégrité",
                            "desc": "Nous garantissons que le document n'a pas été modifié depuis sa création. Il comporte une signature valide et une empreinte immuable enregistrée sur la blockchain Bitcoin, ce qui permet de vérifier son intégrité de manière décentralisée."
                        },
                        {
                            "title": "Authenticité",
                            "desc": "Nous certifions l'identité de l'émetteur. Cela garantit que l'organisation et ses membres sont légitimes et habilités à émettre ce document."
                        },
                        {
                            "title": "Validité",
                            "desc": "Nous garantissons la validité en temps réel du document. Toute modification, qu'il s'agisse d'une mise à jour, d'une révocation, d'une expiration ou d'une prolongation de validité, est immédiatement prise en compte sur cette page."
                        }
                    ]
                },
                "badge": {
                    "title": "Qu'est-ce qu'un badge Smart Certificate ?",
                    "subTitle": "Il s'agit d'un badge qui atteste 3 éléments fondamentaux :",
                    "steps": [
                        {
                            "title": "Intégrité",
                            "desc": "Nous garantissons que le badge n'a pas été modifié depuis sa création. Il respecte la norme Open Badge 2.0."
                        },
                        {
                            "title": "Authenticité",
                            "desc": "Nous certifions l'identité de l'émetteur. Cela garantit que l'organisation et ses membres sont légitimes et habilités à délivrer ce badge."
                        },
                        {
                            "title": "Validité",
                            "desc": "Nous garantissons la validité en temps réel du badge. Toute modification, qu'il s'agisse d'une mise à jour, d'une révocation, d'une expiration ou d'une prolongation de validité, est immédiatement prise en compte sur cette page."
                        }
                    ]
                }
            }
        }
    },
    "pt": {
        "document": {
            "error": {
                "title": "Ocorreu um erro",
                "codes": {
                    "_1": "Ocorreu um erro. O documento a que está a tentar aceder não existe ou foi removido do nosso sistema.",
                    "_2": "O documento a que está a tentar aceder não existe.",
                    "_3": "Este documento foi revogado por {organizationName}. Motivo: {reason}"
                }
            },
            "securedTitle": "Este documento é garantido por <b>Smart Certificate</b>",
            "notSecuredTitle": "Este documento ainda não foi verificado pelo <b>Smart Certificate</b>. Isto pode demorar alguns dias.",
            "actions": {
                "showIntegrity": "Veja como",
                "downloadBlockchainReceipt": "Descarregar o recibo da cadeia de blocos",
                "validateBlockchain": "Validar a receção da cadeia de blocos",
                "downloadDocument": "Descarregar PDF seguro",
                "downloadBadge": "Descarregar insígnia",
                "downloadBadgeAssertion": "Descarregar insígnia (JSON)"
            },
            "badge": {
                "description": "Descrição",
                "criteria": "Critérios",
                "tags": "Etiquetas"
            },
            "recipient": {
                "signature": {
                    "title": "O destinatário assinou o presente documento"
                }
            },
            "blockchain": {
                "popup": {
                    "title": "Verificação da cadeia de blocos",
                    "desc": "Uma prova de validade e integridade para cada documento é armazenada na cadeia de blocos Bitcoin. Isto garante que todos os nossos documentos têm uma prova <b>inalterável</b> da sua integridade armazenada numa plataforma pública e descentralizada. Pode encontrar abaixo o estado da verificação e o carimbo de data/hora, a hora em que a integridade do documento foi armazenada na cadeia de blocos."
                }
            },
            "integrity-dialog": {
                "document": {
                    "title": "O que é um Certificado Inteligente?",
                    "subTitle": "É um documento que certifica 3 coisas fundamentais:",
                    "steps": [
                        {
                            "title": "Integridade",
                            "desc": "Garantimos que o documento não foi alterado desde a sua criação. Contém uma assinatura válida e uma impressão digital imutável armazenada na blockchain de Bitcoin, proporcionando um método descentralizado para verificar a sua integridade."
                        },
                        {
                            "title": "Autenticidade",
                            "desc": "Certificamos a identidade do emitente. Isto garante que a organização e os seus membros são legítimos e estão autorizados a emitir este documento."
                        },
                        {
                            "title": "Validade",
                            "desc": "Garantimos a validade do documento em tempo real. Qualquer alteração, como uma atualização, revogação, caducidade ou prorrogação da validade, é imediatamente refletida nesta página."
                        }
                    ]
                },
                "badge": {
                    "title": "O que é uma insígnia Smart Certificate?",
                    "subTitle": "É uma insígnia que certifica 3 aspetos fundamentais:",
                    "steps": [
                        {
                            "title": "Integridade",
                            "desc": "Garantimos que a insígnia não foi alterada desde a sua criação. Segue a norma Open Badge 2.0."
                        },
                        {
                            "title": "Autenticidade",
                            "desc": "Certificamos a identidade do emitente. Isto garante que a organização e os seus membros são legítimos e estão autorizados a emitir esta insígnia."
                        },
                        {
                            "title": "Validade",
                            "desc": "Garantimos a validade em tempo real da insígnia. Qualquer alteração, como uma atualização, revogação, caducidade ou prorrogação da validade, é refletida instantaneamente nesta página."
                        }
                    ]
                }
            }
        }
    },
    "de": {
        "document": {
            "error": {
                "title": "Es ist ein Fehler aufgetreten",
                "codes": {
                    "_1": "Es ist ein Fehler aufgetreten. Das Dokument, auf das Sie zuzugreifen versuchen, ist entweder nicht vorhanden oder wurde aus unserem System entfernt.",
                    "_2": "Das Dokument, auf das Sie zuzugreifen versuchen, existiert nicht.",
                    "_3": "Dieses Dokument wurde von {organizationName} widerrufen. Grund: {reason}"
                }
            },
            "securedTitle": "Dieses Dokument ist durch ein <b>Smart-Zertifikat</b> gesichert",
            "notSecuredTitle": "Dieses Dokument ist noch nicht von <b>Smart Certificate</b> überprüft worden. Dies kann ein paar Tage dauern.",
            "actions": {
                "showIntegrity": "Erfahren Sie, wie",
                "downloadBlockchainReceipt": "Blockchain-Beleg herunterladen",
                "validateBlockchain": "Blockchain-Empfang validieren",
                "downloadDocument": "Sicheres PDF herunterladen",
                "downloadBadge": "Badge herunterladen",
                "downloadBadgeAssertion": "Badge-Nachweis herunterladen (JSON)"
            },
            "badge": {
                "description": "Beschreibung",
                "criteria": "Kriterien",
                "tags": "Stichworte"
            },
            "recipient": {
                "signature": {
                    "title": "Der Empfänger hat dieses Dokument unterzeichnet"
                }
            },
            "blockchain": {
                "popup": {
                    "title": "Blockchain-Verifizierung",
                    "desc": "Ein Nachweis der Gültigkeit und Integrität jedes Dokuments wird in der Bitcoin-Blockchain gespeichert. Dadurch wird sichergestellt, dass für alle unsere Dokumente ein <b>unveränderlicher</b> Nachweis ihrer Integrität auf einer öffentlichen und dezentralen Plattform gespeichert ist. Unten finden Sie den Status der Überprüfung und den Zeitstempel, den Zeitpunkt, zu dem die Integrität des Dokuments in der Blockchain gespeichert wurde."
                }
            },
            "integrity-dialog": {
                "document": {
                    "title": "Was ist ein Smart Certificate?",
                    "subTitle": "Es ist ein Dokument, das 3 grundlegende Dinge bescheinigt:",
                    "steps": [
                        {
                            "title": "Integrität",
                            "desc": "Wir garantieren, dass das Dokument seit seiner Erstellung nicht verändert wurde. Es enthält eine gültige Signatur und einen unveränderlichen Fingerabdruck, der in der Bitcoin-Blockchain gespeichert ist, wodurch eine dezentrale Methode zur Überprüfung seiner Integrität bereitgestellt wird."
                        },
                        {
                            "title": "Authentizität",
                            "desc": "Wir bestätigen die Identität des Ausstellers. Damit wird sichergestellt, dass die Organisation und ihre Mitglieder legitim und zur Ausstellung dieses Dokuments berechtigt sind."
                        },
                        {
                            "title": "Gültigkeit",
                            "desc": "Wir gewährleisten die Echtzeit-Gültigkeit des Dokuments. Jede Änderung, wie beispielsweise eine Aktualisierung, ein Widerruf, ein Ablauf oder eine Verlängerung der Gültigkeit, wird sofort auf dieser Seite angezeigt."
                        }
                    ]
                },
                "badge": {
                    "title": "Was ist ein Smart Certificate Badge?",
                    "subTitle": "Es ist ein Badge, das 3 grundlegende Dinge bescheinigt:",
                    "steps": [
                        {
                            "title": "Integrität",
                            "desc": "Wir garantieren, dass das Badge seit seiner Erstellung nicht verändert wurde. Es entspricht dem Open Badge 2.0-Standard."
                        },
                        {
                            "title": "Authentizität",
                            "desc": "Wir bestätigen die Identität des Ausstellers. Damit wird sichergestellt, dass die Organisation und ihre Mitglieder legitim und zur Ausstellung dieses Badges berechtigt sind."
                        },
                        {
                            "title": "Gültigkeit",
                            "desc": "Wir stellen sicher, dass die Gültigkeit des Badges in Echtzeit aktuell ist. Jede Änderung, wie beispielsweise eine Aktualisierung, ein Widerruf, ein Ablauf oder eine Verlängerung der Gültigkeit, wird sofort auf dieser Seite angezeigt."
                        }
                    ]
                }
            }
        }
    },
    "nl": {
        "document": {
            "error": {
                "title": "Er is een fout opgetreden",
                "codes": {
                    "_1": "Er is een fout opgetreden. Het document dat u probeert te openen bestaat niet of is verwijderd uit ons systeem.",
                    "_2": "Het document dat u probeert te openen bestaat niet.",
                    "_3": "Dit document is ingetrokken door {organizationName}. Reden: {reason}"
                }
            },
            "securedTitle": "Dit document is beveiligd met een <b>Smart Certificate</b>",
            "notSecuredTitle": "Dit document is nog niet geverifieerd door <b>Smart Certificate</b>. Dit kan een paar dagen duren.",
            "actions": {
                "showIntegrity": "Bekijk hoe",
                "downloadBlockchainReceipt": "Ontvangstbewijs blockchain downloaden",
                "validateBlockchain": "Ontvangst blockchain valideren",
                "downloadDocument": "Veilig PDF downloaden",
                "downloadBadge": "Badge downloaden",
                "downloadBadgeAssertion": "Badgeverklaring downloaden (JSON)"
            },
            "badge": {
                "description": "Beschrijving",
                "criteria": "Criteria",
                "tags": "Tags"
            },
            "recipient": {
                "signature": {
                    "title": "De ontvanger heeft dit document ondertekend"
                }
            },
            "blockchain": {
                "popup": {
                    "title": "Blockchain verificatie",
                    "desc": "Een bewijs van geldigheid en integriteit voor elk document wordt opgeslagen in de Bitcoin blockchain. Dit zorgt ervoor dat al onze documenten een <b>onveranderlijk</b> bewijs van hun integriteit hebben opgeslagen op een openbaar en gedecentraliseerd platform. Hieronder vindt u de status van de verificatie en het tijdstempel, het tijdstip waarop de integriteit van het document is opgeslagen in de blockchain."
                }
            },
            "integrity-dialog": {
                "document": {
                    "title": "Wat is een Smart Certificate?",
                    "subTitle": "Het is een document dat 3 fundamentele zaken certificeert:",
                    "steps": [
                        {
                            "title": "Integriteit",
                            "desc": "Wij garanderen dat het document sinds het aanmaken ervan niet is gewijzigd. Het bevat een geldige handtekening en een onveranderlijke hash die is opgeslagen op de Bitcoin-blockchain, waardoor de integriteit ervan op een gedecentraliseerde manier kan worden gecontroleerd."
                        },
                        {
                            "title": "Authenticiteit",
                            "desc": "Wij verifiëren de identiteit van de uitgever. Dit garandeert dat de organisatie en haar leden legitiem zijn en bevoegd zijn om dit document uit te geven."
                        },
                        {
                            "title": "Geldigheid",
                            "desc": "Wij garanderen de realtime geldigheid van het document. Elke wijziging, zoals een update, intrekking, het verlopen van de geldigheid of een verlenging daarvan, wordt onmiddellijk op deze pagina weergegeven."
                        }
                    ]
                },
                "badge": {
                    "title": "Wat is een Smart Certificate-badge?",
                    "subTitle": "Het is een badge die 3 fundamentele zaken certificeert:",
                    "steps": [
                        {
                            "title": "Integriteit",
                            "desc": "Wij garanderen dat de badge sinds het aanmaken ervan niet is gewijzigd. De badge voldoet aan de Open Badge 2.0-standaard."
                        },
                        {
                            "title": "Authenticiteit",
                            "desc": "Wij verifiëren de identiteit van de uitgever. Dit garandeert dat de organisatie en haar leden legitiem zijn en bevoegd zijn om deze badge uit te geven."
                        },
                        {
                            "title": "Geldigheid",
                            "desc": "Wij garanderen de realtime geldigheid van de badge. Elke wijziging, zoals een update, intrekking, het verlopen van de geldigheid of een verlenging daarvan, wordt onmiddellijk op deze pagina weergegeven."
                        }
                    ]
                }
            }
        }
    }
}
</i18n>

<script>
import Section from '@/components/v3/section/Section.vue'
import SectionHeader from '@/components/v3/section/SectionHeader.vue'
import axios from 'axios'
import { documentServices } from '@/modules/documents/services/DocumentServices'
import { PublicDocument } from '@/modules/documents/models/PublicDocument'
import Loading from '@/components/v3/loading/Loading.vue'
import ImageLoader from '@/components/v3/images/ImageLoader'
import PrimaryButton from '@/components/v3/buttons/PrimaryButton'
import PublicDocumentShareButtons from '@/modules/documents/components/PublicDocumentShareButtons.vue'
import PublicDocumentHeader from './PublicDocumentHeader.vue'
import PublicDocumentTopBackground from './PublicDocumentTopBackground.vue'
import PublicDocumentSmartAd from './PublicDocumentSmartAd.vue'

import { publicDocumentTest } from './../models/_tests.js'

import find from 'lodash/find'

import { getModule } from '@/modules/store'
import ConfigStore from '@/modules/config/store/ConfigStore'
import { User } from '@/modules/users/models/User'

export default {
    components: {
        Section,
        SectionHeader,
        PrimaryButton,
        ImageLoader,
        Loading,
        PublicDocumentShareButtons,
        PublicDocumentHeader,
        PublicDocumentSmartAd,
        PublicDocumentTopBackground
    },
    data() {
        return {
            isLoading: false,
            documentId: '',
            publicDocument: new PublicDocument(),
            testMode: false,
            blockchainDataVisible: false,
            integrityDialogVisible: false,
            error: null,
            badgeAssertion: null,
            badgeClass: null
        }
    },
    mounted() {
        console.log('got', window.location)
        // this.$ga.page(window.location.href)
        // this.documentId = decodeURIComponent('1%7c8d6595ef-6f49-4669-895f-d32b097e22da%7c6f46745d-1e69-4d02-9602-9695c797a5fa')
        this.documentId = decodeURIComponent(window.location.search.substring(1))
        this.getData()
    },
    computed: {
        configStore() {
            return getModule(ConfigStore)
        },
        mainClass() {
            const a = []
            if (this.hasError) a.push('has-error')
            if (!this.isVerified) a.push('not-verified')
            return a.join(' ')
        },
        document() {
            if (this.testMode) return publicDocumentTest
            return this.publicDocument
        },
        documentUrl () {
            return window.location.href
        },
        currentUrl () {
            return window.location.href
        },
        headerClass () {
            return this.isLoading ? 'hidden' : ''
        },
        contentClass () {
            const c = this.topCenterSmartAd ? 'with-top-img' : ''
            return c + ' ' + (this.loading ? 'hidden' : '')
        },
        showTopSmartAds() {
            if (this.showTopSmartAdsAtBottom) return false
            return this.topLeftSmartAd || this.topRightSmartAd
        },
        showBottomTopSmartAds() {
            if (!this.showTopSmartAdsAtBottom) return false
            return this.topLeftSmartAd || this.topRightSmartAd
        },
        showBottomSmartAds() {
            return this.bottomLeftSmartAd || this.bottomRightSmartAd
        },
        topCenterSmartAd() {
            return find(this.smartAds, (o) => o.position === 'top center' && o.isActive)
        },
        topLeftSmartAd() {
            return find(this.smartAds, (o) => o.position === 'top left' && o.isActive)
        },
        topRightSmartAd() {
            return find(this.smartAds, (o) => o.position === 'top right' && o.isActive)
        },
        bottomLeftSmartAd() {
            return find(this.smartAds, (o) => o.position === 'bottom left' && o.isActive)
        },
        bottomRightSmartAd() {
            return find(this.smartAds, (o) => o.position === 'bottom right' && o.isActive)
        },
        documentPreviewUrl() {
            return this.$processUrl(this.document.documentImageUrl)
        },
        smartAds() {
            return this.document.smartads
        },
        recipientName() {
            return this.document.recipientName
        },
        organizationName() {
            return this.document.organizationName
        },
        folderTitle() {
            return this.document.folderTitle
        },
        showBlockchainActions() {
            if (this.testMode) return true
            return this.document && this.document.isBlockChain && this.document.isBlockChainSigned
        },
        blockchainVerificationStatus() {
            if (this.testMode) return true
            return this.showBlockchainActions ? this.document.blockChainReceiptData && this.document.blockChainReceiptData.text : ''
        },
        blockchainVerificationTimestamp() {
            if (this.testMode) return true
            return this.showBlockchainActions ? this.document.blockChainReceiptData && this.document.blockChainReceiptData.timeStamp : ''
        },
        showBlockchainActions2() {
            if (this.testMode) return true
            return this.document && this.document.isBlockChain2 && this.document.isBlockChainSigned2
        },
        blockchainVerificationStatus2() {
            if (this.testMode) return true
            // not receiving this data for recipient
            return false
            // return this.showBlockchainActions2 ? this.document.blockChainReceiptData && this.document.blockChainReceiptData.text : ''
        },
        blockchainVerificationTimestamp2() {
            if (this.testMode) return true
            // not receiving this data for recipient
            return false
            // return this.showBlockchainActions2 ? this.document.blockChainReceiptData && this.document.blockChainReceiptData.timeStamp : ''
        },
        hasError() {
            return this.error != null
        },
        errorData() {
            return this.error.errors && this.error.errors.length > 0 ? this.error.errors[0] : ''
        },
        keywords () {
            return this.document.keywords ? this.document.keywords.split(',') : []
        },
        isVerified() {
            // return true
            return this.document.isVerified
        },
        isBadge() {
            const url = this.document && this.document.documentPdfUrl ? this.document.documentPdfUrl : ''
            return url.toLowerCase().includes('badge')
        },
        securedTitle() {
            if (this.isVerified) {
                return this.$t('document.securedTitle')
            } else {
                return this.$t('document.notSecuredTitle')
            }
        },
        securedIcon() {
            if (this.isVerified) {
                return 's-icon-smart-certificate-logo'
            } else {
                return 's-icon-notification'
            }
        },
        badgeCriteriaNarrative() {
            if (!this.badgeClass || !this.badgeClass.criteria) return ''
            return this.badgeClass.criteria.narrative || ''
        },
        integrityDialogKeyPrefix() {
            return this.isBadge ? 'document.integrity-dialog.badge' : 'document.integrity-dialog.document'
        },
        badgeTags() {
            if (!this.badgeClass || !this.badgeClass.tags) return []
            return this.badgeClass.tags
        }
    },
    methods: {
        getData() {
            this.publicDocument = new PublicDocument()
            this.isLoading = true
            // http://www.Dev.SmartCertificate.com/SmartCertificate/Default.aspx?4LUHu9VcfFg15qLijxuKCXAAdqM9DNDEZfTgYOGeMWjg7D1PQtE0cAUoSxSQgDxy

            documentServices.getUserPublicDocument(this.documentId).then(response => {
                console.log('response', response)

                // check if we need to redirect first
                if (this.checkRedirect(response)) return

                // if not, show data
                this.publicDocument = response

                // const user = new User()
                // user.firstName = this.publicDocument.recipientName
                // this.configStore.setUser(user)

                // this.configStore.setOrganizationData({
                //     organizationName: this.publicDocument.organizationName,
                //     folderName: this.publicDocument.folderTitle,
                //     organizationUrl: ''
                // })

            }).catch(error => {
                this.error = error
                // this.$showNotification(
                //     error.errors,
                //     'error'
                // )
            }).finally(() => {
                this.isLoading = false
                this.fetchBadgeData()
            })

            // UserDocumentApiService.getUserDocumentData(this.documentHash).then(data => {
            //     console.log(data)
            //     this.error = null
            //     this.document = data
            // }).catch(error => {
            //     console.log(error)
            //     this.error = Array.isArray(error) ? error[0] : error
            // }).finally(() => {
            //     this.loading = false
            // })
        },
        dowloadBlockchainReceipt() {
            const url = this.document.blockChainReceiptUrl
            window.open(this.$processUrl(url), '_blank')
            // this.$ga.event(this.gaPageName, 'download-blockchain-receipt', '', 0)
        },
        dowloadDocument() {
            const url = this.document.documentPdfUrl
            window.open(this.$processUrl(url), '_blank')
            // this.$ga.event(this.gaPageName, 'download-document', '', 0)
        },
        downloadBadge() {
            const url = this.documentPreviewUrl
            if (!url) return

            axios.get(url, { responseType: 'blob' }).then(response => {
                const blobUrl = window.URL.createObjectURL(response.data)
                const extension = response.data.type && response.data.type.includes('/') ? `.${response.data.type.split('/')[1]}` : ''
                const link = window.document.createElement('a')
                link.href = blobUrl
                link.download = `badge${extension}`
                window.document.body.appendChild(link)
                link.click()
                window.document.body.removeChild(link)
                window.URL.revokeObjectURL(blobUrl)
            }).catch(() => {
                window.open(url, '_blank')
            })
        },
        showBlockchainData () {
            this.blockchainDataVisible = true
            // this.$ga.event(this.gaPageName, 'show-blockchain-data', '', 0)
        },
        sendUserDocumentActivity (type, smartAdType = null) {
            console.log('sendUserDocumentActivity', type, smartAdType)
            UserDocumentApiService.addUserDocumentActivity(this.document.documentRowId, type, smartAdType)
        },
        showIntegrityDialog() {
            this.integrityDialogVisible = true
        },
        fetchBadgeData() {
            if (!this.isBadge || !this.document.documentPdfUrl) return
            const assertionUrl = this.$processUrl(this.document.documentPdfUrl)
            axios.get(assertionUrl).then(response => {
                this.badgeAssertion = response.data
                const badgeClassUrl = this.badgeAssertion.badge
                if (badgeClassUrl) {
                    return axios.get(badgeClassUrl)
                }
            }).then(response => {
                if (response && response.data) {
                    this.badgeClass = response.data
                }
            }).catch(error => {
                console.error('Failed to fetch badge data', error)
            })
        },
        checkRedirect(documentData) {
            console.log('checkRedirect', documentData.isPublicDocumentRedirectActive, documentData.publicDocumentRedirectUrl, documentData.organizationWebsiteUrl, window.location, window.parent.location, window.location !== window.parent.location)

            // if we are in an iframe, stop redirecting
            // we don't want to check further as to avoid cross origin issues
            if (window.self !== window.top) return false

            // check if we need to redirect user to organization website
            if (documentData.isPublicDocumentRedirectActive) {
                const url = documentData.publicDocumentRedirectUrl || documentData.organizationWebsiteUrl
                // const urlTo = new URL(url)
                // const urlFrom = new URL(window.parent.location.href)
                // console.log('checkRedirect B', urlTo, urlFrom, urlTo.host, urlFrom.host, urlTo.host === urlFrom.host)
                // if host is same, don't redirect
                // if (urlTo.host === urlFrom.host) return false
                window.location.href = url + '?' + window.location.href
                return true
            }
            return false
        }
    }
}
</script>

<style lang="stylus">
@import "~assets/v3/css/variables.styl";
@import "./../css/public-document.styl";

.document

    .section-content
        width 100% !important
        min-height 100% !important
        // padding 0 $outside-padding $outside-padding $outside-padding !important
        responsive-styling-auto-scale(padding, $outside-padding, $responsive-max-width)
        padding-top 0 !important
        max-width 100% !important
        align-items center
        responsive-styling-auto-scale(gap, 10, $responsive-max-width)
        justify-content flex-start !important

    &.has-error

        .section-content
            justify-content center !important

    .document-header
        z-index 10
        responsive-styling-auto-scale(margin-top, 190, $responsive-max-width)
        width 100%
        max-width $content-max-width !important

    .document-smartads
        display flex
        flex-direction row
        justify-content space-between
        width 100%
        max-width $content-max-width !important

    .document-container
        // min-height 500px
        responsive-styling-auto-scale(min-height, 500, $responsive-max-width)
        height 100%
        // flex 0 1 auto
        background-color #FBFBFB
        width 100%
        max-width $content-max-width !important
        border-radius 5px
        box-sizing border-box
        box-shadow 0px 2px 8px rgba(163, 163, 164, 0.25)
        // padding 44px 74px 54px 74px
        responsive-styling-auto-scale(padding-top, 44, $responsive-max-width)
        responsive-styling-auto-scale(padding-right, 74, $responsive-max-width)
        responsive-styling-auto-scale(padding-bottom, 54, $responsive-max-width)
        responsive-styling-auto-scale(padding-left, 74, $responsive-max-width)

        .document-container-frame
            display inline-block
            position relative
            width 100%
            height 100%
            display flex
            flex-direction column
            responsive-styling-auto-scale(gap, 32, $responsive-max-width)

        .document-secured-header
            position relative
            background-color $bg-blue
            border-radius 5px
            display flex
            align-items center
            color white
            box-sizing border-box
            padding 4px 4px 4px 54px
            justify-content space-between
            responsive-styling-auto-scale(padding-left, 54, $responsive-max-width)
            responsive-styling-auto-scale(font-size, 16, $responsive-max-width)

            .primary-button
                font-size inherit
                min-height auto
                responsive-styling-auto-scale(padding-top, 5, $responsive-max-width)
                responsive-styling-auto-scale(padding-bottom, 5, $responsive-max-width)
                responsive-styling-auto-scale(padding-left, 8, $responsive-max-width)
                responsive-styling-auto-scale(padding-right, 8, $responsive-max-width)

                i
                    font-size inherit

            .document-secured-header-logo
                position absolute
                top 50%
                left 0
                transform translate(-50%, -50%)
                background-color $bg-blue
                box-shadow 0px 3px 4px rgba(0, 0, 0, 0.15)
                z-index 1
                width 74px
                height 74px
                border-radius 100px
                responsive-styling-auto-scale(width, 74, $responsive-max-width)
                responsive-styling-auto-scale(height, 74, $responsive-max-width)

                i
                    position absolute
                    top 50%
                    left 50%
                    transform translate(-50%, -50%)
                    // font-size 32px
                    responsive-styling-auto-scale(font-size, 32, $responsive-max-width)

        .document-container-frame-picture
            line-height 0
            text-align center
            // margin-top 32px
            // responsive-styling-auto-scale(margin-top, 32, $responsive-max-width)

            .loading
                z-index 1

            img
                max-width 100%
                max-height 100%

        .document-recipient-signature
            padding 12px
            border-radius 4px
            color $bg-blue
            text-align center
            background-color #eeeeee
            font-weight bold

        .document-actions
            max-width $inside-width
            width 100%
            box-sizing border-box
            display flex
            justify-content space-between
            // margin-top 32px
            // responsive-styling-auto-scale(margin-top, 32, $responsive-max-width)
            responsive-styling-auto-scale(font-size, 16, $responsive-max-width)

            .primary-button
                font-size inherit
                min-height auto
                responsive-styling-auto-scale(padding-top, 10, $responsive-max-width)
                responsive-styling-auto-scale(padding-bottom, 10, $responsive-max-width)
                responsive-styling-auto-scale(padding-left, 16, $responsive-max-width)
                responsive-styling-auto-scale(padding-right, 16, $responsive-max-width)

                img
                    responsive-styling-auto-scale(height, 24, $responsive-max-width)

                span+img
                    responsive-styling-auto-scale(margin-left, 8, $responsive-max-width)

            button:not(:last-child)
                margin-right 10px

            & .blink

                span
                    animation blink 1s infinite

                    @keyframes blink
                        0%
                            opacity 1.0
                            // color $color-primary
                        50%
                            opacity 0.6
                            // color $color-red-1
                        100%
                            opacity 1.0
                            // color $color-primary


        .document-badge-metadata
            width 100%
            box-sizing border-box
            background-color white
            border-radius 5px
            border 1px solid #e8e8e8
            responsive-styling-auto-scale(padding, 24, $responsive-max-width)

            .document-badge-metadata-title
                margin 0
                responsive-styling-auto-scale(font-size, 22, $responsive-max-width)
                color #333

            .document-badge-metadata-field
                responsive-styling-auto-scale(font-size, 15, $responsive-max-width)
                color #555
                responsive-styling-auto-scale(margin-top, 8, $responsive-max-width)
                margin-bottom 0

                strong
                    color #333

            .document-badge-metadata-tags
                responsive-styling-auto-scale(margin-top, 12, $responsive-max-width)
                display flex
                flex-wrap wrap
                responsive-styling-auto-scale(gap, 8, $responsive-max-width)

                span
                    border-radius 5px
                    background-color $color-blue-3
                    color white
                    responsive-styling-auto-scale(padding-top, 6, $responsive-max-width)
                    responsive-styling-auto-scale(padding-bottom, 6, $responsive-max-width)
                    responsive-styling-auto-scale(padding-left, 10, $responsive-max-width)
                    responsive-styling-auto-scale(padding-right, 10, $responsive-max-width)
                    responsive-styling-auto-scale(font-size, 13, $responsive-max-width)
                    opacity 0.8

        .document-keywords
            display flex
            flex-wrap wrap
            responsive-styling-auto-scale(gap, 10, $responsive-max-width)

            span
                border-radius 5px
                background-color $color-blue-3
                color white
                flex 0 0 auto
                // padding 8px 12px
                responsive-styling-auto-scale(padding-top, 12, $responsive-max-width)
                responsive-styling-auto-scale(padding-bottom, 12, $responsive-max-width)
                responsive-styling-auto-scale(padding-left, 12, $responsive-max-width)
                responsive-styling-auto-scale(padding-right, 12, $responsive-max-width)
                display inline-block
                opacity 0.6
                responsive-styling-auto-scale(font-size, 16, $responsive-max-width)

        .document-share-buttons
            z-index 2

            &.vertical
                position absolute
                z-index 2
                top 70px
                responsive-styling-auto-scale(right, -56, $responsive-max-width)

                @media screen and (max-width: 900px)
                    display none

                .ring-button
                    responsive-styling-auto-scale(width, 40, $responsive-max-width)
                    responsive-styling-auto-scale(height, 40, $responsive-max-width)

                    .icon
                        responsive-styling-auto-scale(font-size, 18, $responsive-max-width)

            &.boxed
                display none
                justify-content center
                align-items center

                @media screen and (max-width: 900px)
                    display flex

                .ring-button
                    responsive-styling-auto-scale(width, 54, $responsive-max-width)
                    responsive-styling-auto-scale(height, 54, $responsive-max-width)

                    .icon
                        responsive-styling-auto-scale(font-size, 18, $responsive-max-width)

    .document-error
        justify-self center !important
        display flex
        flex-flow column
        justify-content center
        align-items center
        word-break inherit

        h1
            font-weight bold
            // font-size 60px
            // line-height 46px
            responsive-styling-auto-scale(font-size, 60, $responsive-max-width)
            responsive-styling-auto-scale(line-height, 46, $responsive-max-width)
            background linear-gradient(360deg, #FFD25F -44%, #23D7FF 115%)
            -webkit-background-clip text
            -webkit-text-fill-color transparent
            box-decoration-break clone
            -webkit-box-decoration-break clone
            margin-bottom 8px
            text-align center

        h2
            margin-top 8px
            responsive-styling-auto-scale(font-size, 36, $responsive-max-width)
            text-align center

    &.not-verified

        .document-secured-header
        .document-secured-header-logo
            background-color $color-grey-4 !important

.document-blockchain-data-dialog
    box-shadow 0 8px 32px rgba(0, 0, 0, 0.3)
    background-color $bg-blue
    display flex
    flex-flow row
    justify-content center

    .el-dialog__header
        display none

    .el-dialog__body
        color white
        word-break inherit
        responsive-styling-auto-scale(padding, 64, $responsive-max-width)
        responsive-styling(font-size, 8, 16, 132, $responsive-max-width)

        i
            responsive-styling-auto-scale(margin-right, 16, $responsive-max-width)

        h1
            margin-top 0
            responsive-styling-auto-scale(font-size, 32, $responsive-max-width)
            responsive-styling-auto-scale(margin-bottom, 21, $responsive-max-width)

        .document-blockchain-data-dialog-proof
            background-color alpha(white, 0.1)
            responsive-styling-auto-scale(margin-top, 24, $responsive-max-width)
            border-radius 5px
            responsive-styling-auto-scale(padding, 8, $responsive-max-width)

$qr-code-max-width = 60%
$qr-code-width = 300px

.qr-code
    margin-top 40px
    margin-bottom 40px
    max-width $qr-code-max-width
    width $qr-code-width
    box-sizing border-box
    height 0
    padding-bottom "calc(min(%s, %s))" % ($qr-code-max-width $qr-code-width)
    overflow hidden
    display none

    @media screen and (max-width: 800px)
        display block

    > *
        max-width 100%
        object-position top
        object-fit contain

.document-integrity-dialog
    box-shadow 0 8px 32px rgba(0, 0, 0, 0.3)
    responsive-styling-auto-scale(border-radius, 12px, $responsive-max-width)
    background-color $bg-blue
    display flex
    flex-flow row
    justify-content center
    responsive-styling(width, 80%, 50%, 137, $responsive-max-width)

    .el-dialog__header
        display none

    .el-dialog__body
        color white
        responsive-styling-auto-scale(padding, 64, $responsive-max-width)
        responsive-styling(font-size, 8, 16, 132, $responsive-max-width)
        word-break inherit

        span
            display inline-block

        i
            responsive-styling-auto-scale(margin-right, 16, $responsive-max-width)

        h1
            margin-top 0
            responsive-styling-auto-scale(font-size, 32, $responsive-max-width)
            responsive-styling-auto-scale(margin-bottom, 21, $responsive-max-width)

        h2
            responsive-styling-auto-scale(font-size, 16, $responsive-max-width)
            responsive-styling-auto-scale(margin-top, 32, $responsive-max-width)
            responsive-styling-auto-scale(margin-bottom, 16, $responsive-max-width)
</style>



// WEBPACK FOOTER //
// src/modules/documents/components/PublicDocument.vue